// BMEX stored-value card — navy + gold with black depth. In-loop, balance-only.
// The register scans the QR and pulls the amount (Starbucks-card model).
import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";

const GOLD = "#c9a227";
const GOLD_SOFT = "#e6c765";

export function WalletCard({ card, onRotate, rotating, onClaim, claiming, claimMsg }) {
  const [showQR, setShowQR] = useState(false);
  if (!card) return null;
  return (
    <div className="space-y-2">
      <h3 className="text-[10px] uppercase tracking-widest text-stone-500">BMEX card — stored value</h3>

      <button
        data-testid="member-card"
        onClick={() => setShowQR((v) => !v)}
        className="block w-full text-left rounded-2xl p-[2px] transition-transform active:scale-[0.99]"
        style={{ background: `linear-gradient(135deg, ${GOLD} 0%, #6e5410 45%, ${GOLD_SOFT} 100%)` }}
      >
        <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: "1264 / 799" }}>
          <img src="/assets/bmex-card-template.jpg" alt="BMEX card" className="absolute inset-0 w-full h-full object-cover" />
          {/* chip + contactless are baked into the card art itself */}
          <div className="absolute left-[3%] bottom-[26%]">
            <p className="text-[10px] sm:text-xs font-serif font-bold tracking-[0.2em] text-stone-100 uppercase">{card.label}</p>
            {card.business && card.business !== card.label && (
              <p className="text-[8px] sm:text-[10px] font-serif tracking-[0.15em] uppercase" style={{ color: "rgba(230,199,101,0.75)" }}>{card.business}</p>
            )}
          </div>
          <div className="absolute right-[7%] bottom-[26%] text-right">
            <p className="text-[7px] sm:text-[8px] font-serif uppercase tracking-widest" style={{ color: "rgba(230,199,101,0.55)" }}>Balance</p>
            <p className="text-xs sm:text-sm font-serif font-bold" style={{ color: GOLD_SOFT }} data-testid="card-balance">
              {card.susd_balance == null ? "—" : `$${card.susd_balance.toLocaleString("en-US", { minimumFractionDigits: 2 })}`}
            </p>
          </div>
          <p className="absolute left-[9%] right-[7%] bottom-[11%] text-[11px] sm:text-sm font-serif tracking-[0.35em] text-stone-100" data-testid="card-number">
            {card.card_number}
          </p>
        </div>
      </button>

      {/* stars + rewards — interchangeable with load dollars */}
      <div className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3" data-testid="rewards-panel">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-stone-100" data-testid="stars-balance">★ {card.stars_available ?? 0} stars</span>
          <span className="text-[10px] font-mono text-stone-500" data-testid="loaded-total">
            lifetime loaded ${(card.loaded_total_usd ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </span>
        </div>
        {card.rewards_pending > 0 && (
          <p className="rounded-lg border border-emerald-500/50 bg-emerald-950/30 px-3 py-2 text-xs text-emerald-300" data-testid="rewards-pending">
            {card.rewards_pending} reward{card.rewards_pending > 1 ? "s" : ""} waiting — show at any BMEX register to claim
          </p>
        )}
        {(card.reward_catalog || []).map((r) => {
          const affordable = (card.stars_available ?? 0) >= r.stars;
          return (
            <div key={r.kind} className="flex items-center justify-between gap-3">
              <p className="text-[11px] text-stone-400 leading-snug flex-1">{r.label}</p>
              <button
                data-testid={`claim-${r.kind.toLowerCase().replaceAll("_", "-")}`}
                onClick={() => onClaim(r.kind)}
                disabled={!affordable || claiming}
                className="rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors disabled:opacity-40"
                style={{ background: affordable ? "#e6c765" : "#292524", color: affordable ? "#0a1a33" : "#78716c" }}
              >
                {r.stars.toLocaleString()}★
              </button>
            </div>
          );
        })}
        <p className="text-[9px] font-mono text-stone-600 uppercase tracking-widest">
          Earn 1★ per $1 spent · loads and stars unlock the same rewards
        </p>
        {claimMsg && (
          <p className="text-xs leading-relaxed" style={{ color: "#e6c765" }} data-testid="claim-message">{claimMsg}</p>
        )}
      </div>

      {showQR && (
        <div className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3" data-testid="card-qr-panel">
          <div className="mx-auto w-fit rounded-xl bg-white p-3">
            <QRCodeSVG data-testid="card-qr" value={card.qr_payload} size={170} />
          </div>
          <p className="text-[11px] text-center text-stone-400">
            The register scans this and pulls the amount — like a Starbucks card. Balance-only; an empty wallet declines.
          </p>
          <div className="flex gap-2">
            <button
              data-testid="card-rotate-button"
              onClick={onRotate}
              disabled={rotating}
              className="flex-1 rounded-lg border border-stone-700 py-2.5 text-xs text-stone-300 hover:border-amber-500/50 transition-colors disabled:opacity-50"
            >
              {rotating ? "Replacing…" : "Replace card (kills this QR)"}
            </button>
          </div>
          <p className="text-[10px] text-center text-stone-600" data-testid="card-apple-wallet-note">
            Add to Apple Wallet / Google Wallet activates with our pass-signing enrollment — the QR above works everywhere today.
          </p>
        </div>
      )}
    </div>
  );
}
